import { ref, watch, computed, onUnmounted } from 'vue';
import { MdTypography, MdModal, MdButton } from '@antiqua-dls/vue-components';
import { debounce } from 'lodash';
import { useForm } from 'vee-validate';
import agGrid from 'components/agGrid/index.vue';
import TextField from 'src/components/form/new-text-field/index.vue';
import { AG_GRID_ROW_HEIGHT } from 'src/utils/constants/common';

export default {
  name: 'EditInformationModal',
  components: {
    MdTypography,
    MdModal,
    MdButton,
    TextField,
    agGrid
  },
  props: {
    modalTexts: {
      type: Object,
      required: true
    },
    showEditModal: {
      type: Boolean,
      default: false
    },
    bordered: {
      type: Boolean,
      default: false
    },
    tableHeaders: {
      type: Object,
      required: true
    },
    rowData: {
      type: Object,
      default: []
    },
    inputField: {
      type: String,
      default: ''
    },
    inputConfig: {
      type: Object,
      default: {
        maxLength: 0,
        showOptionalText: false,
        placeholder: '',
        optionalText: '',
        keyInputHandler: null,
        validationRules: ''
      }
    }
  },
  setup(props, { emit }) {
    // store and composables
    const { errors, validate } = useForm();
    // refs
    const inputValue = ref('');
    const disableSubmit = ref(true);
    const rowData = ref([]);
    // computes
    const minHeightStyle = computed(() => `min-height: ${Math.min(props.rowData.length * AG_GRID_ROW_HEIGHT, 360)}px;`);
    const colDefs = computed(() => {
      return [
        {
          headerName: props.tableHeaders.name,
          field: 'name',
          flex: 2,
          suppressMovable: true,
          resizable: false,
          wrapHeaderText: true,
          sortable: false
        },
        {
          headerName: props.tableHeaders.value,
          field: 'value',
          suppressMovable: true,
          resizable: false,
          wrapHeaderText: true,
          sortable: false
        }
      ];
    });

    // methods
    const closeModal = () => {
      disableSubmit.value = true;
      inputValue.value = '';
      rowData.value = [];
      emit('update:showEditModal', false);
    };

    const handleKeyInput = event => {
      if (props.inputConfig?.keyInputHandler) {
        props.inputConfig.keyInputHandler(event);
      }
    };

    const handleKeyUpPress = debounce(() => {
      validate();
      const errorState = errors.value[`${props.inputField}-bulkEdit-input`];
      disableSubmit.value = errorState || !inputValue.value;

      let updatedValue = [];
      for (const row of rowData.value) {
        let fixedValue = row.fixedValue || '';
        fixedValue = Array.isArray(fixedValue) ? fixedValue : fixedValue.split(',');
        let newInputValue = [...fixedValue, ...inputValue.value.split(',')];
        newInputValue = newInputValue.map(val => val.trim()).filter(Boolean);
        updatedValue.push({
          ...row,
          value: Array.from(new Set(newInputValue)).join(', ')
        });
      }
      rowData.value = updatedValue;
    }, 100);

    const setUpdatedData = () => {
      emit('update:valueUpdated', rowData.value);
      emit('update:showEditModal', false);
    };

    watch(
      () => props.rowData,
      () => {
        rowData.value = JSON.parse(JSON.stringify(props.rowData));
      },
      { immediate: true, deep: true }
    );

    onUnmounted(() => {
      closeModal();
    });

    return {
      errors,
      inputValue,
      closeModal,
      colDefs,
      rowData,
      disableSubmit,
      setUpdatedData,
      handleKeyInput,
      handleKeyUpPress,
      minHeightStyle
    };
  }
};
